import { log, logV } from "../../services/debug-logger";
import type { ExcelGridCellEditorProps } from "../models/excel-grid-component-props.models";
import { getCellEditorRows } from "../utils/excel-like-grid-ui.utils";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";

export const ExcelGridCellEditor = ({
  value,
  cellEditorMode,
  onCancel,
  onCommit,
  onValueChange,
}: ExcelGridCellEditorProps) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const hasCommittedRef = useRef(false);
  const isSingleLineEditor = cellEditorMode === "singleLine";
  const editorRows = useMemo(
    () => (isSingleLineEditor ? 1 : getCellEditorRows(value)),
    [isSingleLineEditor, value],
  );

  logV("ExcelGridCellEditor - BUILD", { rows: editorRows, length: value.length, mode: cellEditorMode });

  const commitOnce = (shouldFocusGrid = false) => {
    if (hasCommittedRef.current) {
      logV("ExcelGridCellEditor - commitOnce - duplicate");
      return;
    }

    hasCommittedRef.current = true;
    log("ExcelGridCellEditor - commitOnce", { shouldFocusGrid });
    onCommit(shouldFocusGrid);
  };

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.focus();
    textarea.select();
    logV("ExcelGridCellEditor - INIT");
  }, []);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    if (isSingleLineEditor) {
      textarea.style.height = "";
      return;
    }

    textarea.style.height = "auto";
    if (textarea.scrollHeight > 0) {
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [isSingleLineEditor, value]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      rows={editorRows}
      aria-label="Edit selected cell"
      title="Edit selected cell"
      data-test="excel-grid-cell-editor"
      onBlur={() => {
        log("ExcelGridCellEditor - onBlur");
        commitOnce();
      }}
      onChange={(event) => onValueChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter" && (isSingleLineEditor || !event.shiftKey)) {
          event.preventDefault();
          log("ExcelGridCellEditor - onKeyDown - enter");
          commitOnce(true);
        }

        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          log("ExcelGridCellEditor - onKeyDown - escape");
          onCancel();
        }
      }}
      className={twMerge(
        "excel-grid-cell-editor m-0 block w-full select-text resize-none border-0 bg-white p-0 text-xs leading-4 text-inherit outline-none dark:bg-gray-800",
        isSingleLineEditor ? "h-4 min-h-0 overflow-hidden" : "min-h-4 overflow-hidden",
      )}
    />
  );
};
